<template>
  <div class="api-errors-reports-container">
    <ApiErrorsFilters
      ref="filters"
      :apiErrorsData="filteredApiErrors"
      @filter-change="onFilterChange"
    />
    <div class="reports-wrap">
      <div class="bar-section full-width">
        <h5 class="chart-title">
          {{ $t('API_ERRORS_REPORT.CHART.ERRORS_BY_DATE') }}
        </h5>
        <ApiErrorsByDateBarChart :apiErrors="filteredApiErrors" />
      </div>
      <div class="bar-section-group">
        <div class="bar-section wide">
          <h5 class="chart-title">
            {{ $t('API_ERRORS_REPORT.CHART.TOP_REASONS') }}
          </h5>
          <ApiErrorsReasonsTable :apiErrors="filteredApiErrors" />
        </div>
        <div class="bar-section narrow">
          <h5 class="chart-title">
            {{ $t('API_ERRORS_REPORT.CHART.IS_TEMPLATE') }}
          </h5>
          <ApiErrorsTemplatePieChart :apiErrors="filteredApiErrors" />
        </div>
      </div>
      <div class="whatsapp-api-info">
        <p>
          <strong>{{ $t('API_ERRORS_REPORT.INFO.MESSAGE') }}</strong
          ><br />
          <a
            :href="$t('API_ERRORS_REPORT.INFO.LINK')"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('API_ERRORS_REPORT.INFO.LINK') }}
          </a>
        </p>
      </div>
      <div class="table-section no-bg">
        <ApiErrorsTable :apiErrorsData="filteredApiErrors" />
      </div>
    </div>
  </div>
</template>

<script>
import ApiErrorsTable from './components/ApiErrorsTable';
import ApiErrorsFilters from './components/ApiErrorsFilters';
import ApiErrorsReportApi from '../../../../api/inbox/historyApiErrors';
import ApiErrorsByDateBarChart from './components/ApiErrorsByDateBarChart';
import ApiErrorsReasonsTable from './components/ApiErrorsReasonsTable';
import ApiErrorsTemplatePieChart from './components/ApiErrorsTemplatePieChart';

export default {
  name: 'ApiErrorsReportsView',
  components: {
    ApiErrorsFilters,
    ApiErrorsTable,
    ApiErrorsByDateBarChart,
    ApiErrorsReasonsTable,
    ApiErrorsTemplatePieChart,
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      apiErrors: [],
      filters: {
        customDateRange: [sevenDaysAgo, today],
        inboxes: [],
        errors: [],
        users: [],
        isTemplates: [],
      },
    };
  },
  computed: {
    filteredApiErrors() {
      const {
        customDateRange = [],
        inboxes = [],
        errors = [],
        users = [],
        isTemplates = [],
      } = this.filters;

      const [start, end] = customDateRange;

      return this.apiErrors.filter(item => {
        const createdDate = new Date(item.created_at);
        const createdDateOnly = new Date(
          createdDate.getFullYear(),
          createdDate.getMonth(),
          createdDate.getDate()
        );

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        const dateMatch =
          !startDate ||
          !endDate ||
          (createdDateOnly >= startDate && createdDateOnly <= endDate);

        const inboxMatch = !inboxes.length || inboxes.includes(item.inbox_name);

        const errorMatch = !errors.length || errors.includes(item.error);

        const userMatch = !users.length || users.includes(item.sender_name);

        const templateMatch =
          !isTemplates.length ||
          isTemplates.includes(item.is_template?.toLowerCase());

        return (
          dateMatch && inboxMatch && errorMatch && userMatch && templateMatch
        );
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.filters.emitFilters();
    });
  },
  methods: {
    prepareApiErrors(rawMessages) {
      if (!Array.isArray(rawMessages)) return [];

      return rawMessages.map(msg => {
        const link_conversation = `${window.location.origin}/app/accounts/${msg.account_id}/conversations/${msg.display_id}?messageId=${msg.message_id}`;
        return {
          ...msg,
          link_conversation,
        };
      });
    },
    async onFilterChange(newFilters) {
      this.filters = {
        ...this.filters,
        ...newFilters,
      };

      try {
        const [start, end] = this.filters.customDateRange || [];
        const formatDate = date => new Date(date).toISOString().split('T')[0];

        const response = await ApiErrorsReportApi.getAll({
          startDate: start ? formatDate(start) : null,
          endDate: end ? formatDate(end) : null,
        });

        const messages = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        this.apiErrors = this.prepareApiErrors(messages);
      } catch (error) {
        console.error(this.$t('API_ERRORS_REPORT.ERRORS.FETCH'), error);
      }
    },
  },
};
</script>

<style scoped>
.api-errors-reports-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  align-items: flex-start;
}

.api-errors-reports-container > *:first-child {
  width: 280px;
  flex-shrink: 0;
  margin-right: 24px;
}

.reports-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2vh 2vw 10vh;
  background: #f8fafc;
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;
}

.bar-section,
.table-section {
  margin-top: 4vh;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  width: 100%;
  overflow-x: auto;
}

.table-section.no-bg {
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.bar-section-group {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 1vh;
}

.bar-section-group .bar-section {
  flex: 1 1 auto;
  max-height: 500px;
  overflow: hidden;
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.05);
}

.chart-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
}

.bar-section.full-width {
  flex: 1 1 100%;
  margin-top: 2vh;
}

.bar-section.wide {
  flex: 1 1 70%;
  max-height: 500px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.bar-section.narrow {
  flex: 1 1 28%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.whatsapp-api-info {
  margin-top: 20px;
  background-color: #333;
  color: white;
  padding: 20px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
}

.whatsapp-api-info a {
  color: #4fc3f7;
  text-decoration: underline;
  word-break: break-word;
}
</style>
